/**
 * 전역 보안·접근정책 훅 (TanStack Query).
 *
 * 보안·접근정책은 워크스페이스·회사 단위가 아니라 <b>플랫폼 전역</b>('(System)' 회사 companies.settings) 단위이며 앱단에서 집행함.
 *
 *   GET  /security-policy         공개 안전값(비번 최소길이·세션·잠금 임계) — 미인증 포함 조회
 *   GET  /admin/security-policy   전역 정책 전체 — 시스템 어드민(SuperAdmin)
 *   PUT  /admin/security-policy   전역 정책 부분 갱신 — 시스템 어드민(SuperAdmin, 서버가 병합·감사)
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import { DEFAULT_PASSWORD_MIN_LENGTH } from "@/lib/passwordPolicy";

/** 전역 보안정책 공개 안전값(FE 비번 최소길이·세션 타이머·로그인 안내 문구용). 계정별 상태는 미포함. */
export interface SecurityPolicyPublic {
  /** 비밀번호 최소 길이(pwpol 티어 파생: std 8·strong 12·max 14) */
  passwordMinLength: number;
  /** 세션 유휴 타임아웃 표기(예: "30분", 미설정 null) */
  session: string | null;
  /** 로그인 실패 잠금 임계 표기(예: "5회", 미설정 null) */
  lockout: string | null;
}

/** 전역 보안정책 전체(SuperAdmin 관리용). */
export interface GlobalSecurityPolicy {
  /** 로그인 실패 잠금 임계("5회"·"10회"·"제한 없음") */
  lockout: string | null;
  /** 잠금 유지 시간(분) */
  lockoutMinutes: number | null;
  /** 비밀번호 정책 티어("std"·"strong"·"max") */
  pwpol: string | null;
  /** 세션 유휴 타임아웃("30분"·"60분") */
  session: string | null;
}

const publicKey = ["security-policy", "public"] as const;
const adminKey = ["security-policy", "admin"] as const;

/** 공개 전역 보안정책 조회(미인증 포함). 로그인 화면·비번 폼·유휴 타이머에서 사용. */
export function useSecurityPolicyPublic() {
  return useQuery({
    queryKey: publicKey,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: () => apiFetch<SecurityPolicyPublic>(`/api/security-policy`),
  });
}

/**
 * 전역 보안정책의 비밀번호 최소 길이 — 비밀번호 입력 폼(변경·재설정·초대수락·자가가입)의 단일 창구.
 * 정책 미로드(로딩·오류) 시 기본값({@link DEFAULT_PASSWORD_MIN_LENGTH})으로 폴백하며, 최종 집행은 서버가 함.
 */
export function usePasswordMinLength(): number {
  const { data } = useSecurityPolicyPublic();
  return data?.passwordMinLength ?? DEFAULT_PASSWORD_MIN_LENGTH;
}

/** 전역 보안정책 조회 — SuperAdmin 관리 화면. */
export function useGlobalSecurityPolicy() {
  return useQuery({
    queryKey: adminKey,
    staleTime: 30 * 1000,
    queryFn: () => apiFetch<GlobalSecurityPolicy>(`/api/admin/security-policy`),
  });
}

/** 전역 보안정책 부분 갱신 — SuperAdmin(서버가 병합·감사). */
export function useUpdateGlobalSecurityPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<GlobalSecurityPolicy>) =>
      apiFetch<GlobalSecurityPolicy>(`/api/admin/security-policy`, {
        method: "PUT",
        body: patch,
      }),
    onSuccess: (data) => {
      qc.setQueryData(adminKey, data);
      void qc.invalidateQueries({ queryKey: publicKey });
    },
  });
}
